import React from 'react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
import { motion } from 'framer-motion';
import { ShieldCheck, CreditCard, CheckCircle2, Wallet } from 'lucide-react';
import { useScreenInit } from '../useScreenInit';
export function FinancingPage() {
  useScreenInit();
  return <div className="min-h-screen bg-white" data-id="element-229">
      <Header data-id="element-230" />

      <main className="pt-40 pb-24" data-id="element-231">
        <div className="max-w-4xl mx-auto px-4" data-id="element-232">
          {/* Hero */}
          <div className="text-center mb-16" data-id="element-233">
            <div className="inline-flex items-center justify-center w-20 h-20 bg-teal-50 rounded-full mb-6" data-id="element-234">
              <Wallet className="text-[#0D9488]" size={40} data-id="element-235" />
            </div>
            <motion.h1 initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} className="text-4xl md:text-6xl font-bold text-[#0F2C59] mb-6" data-id="element-236">
              Financing & Insurance
            </motion.h1>
            <p className="text-xl text-[#4F709C] leading-relaxed" data-id="element-237">
              We believe everyone deserves a healthy smile. We offer flexible
              payment options to ensure financial barriers never stand in the
              way of your care.
            </p>
          </div>

          <div className="space-y-12" data-id="element-238">
            {/* Insurance Section */}
            <section className="bg-slate-50 p-8 md:p-12 rounded-[12px] border border-slate-100" data-id="element-239">
              <div className="flex items-center gap-3 mb-6" data-id="element-240">
                <ShieldCheck className="text-[#0D9488]" size={32} data-id="element-241" />
                <h2 className="text-3xl font-bold text-[#0F2C59]" data-id="element-242">
                  Accepted Insurance Providers
                </h2>
              </div>
              <p className="text-slate-600 leading-relaxed mb-6" data-id="element-243">
                We accept most dental insurances! If you are unsure of your
                coverage, we can help you understand your benefits and expected
                costs. When dental care is needed, we will walk you through all
                costs involved before scheduling treatment. We will also work
                with your dental insurance company to get the most out of your
                benefits.
              </p>
              <p className="text-[#0F2C59] font-bold" data-id="element-244">
                If you have any insurance questions, let us know!
              </p>
            </section>

            {/* Payment Options Section */}
            <section className="bg-white p-8 md:p-12 rounded-[12px] border-2 border-[#0F2C59] shadow-xl" data-id="element-245">
              <div className="flex items-center gap-3 mb-8" data-id="element-246">
                <CreditCard className="text-[#0D9488]" size={32} data-id="element-247" />
                <h2 className="text-3xl font-bold text-[#0F2C59]" data-id="element-248">
                  Payment Options for Patients
                </h2>
              </div>

              <ul className="space-y-4 mb-10" data-id="element-249">
                {['Cash or check', 'Visa / MasterCard / AMEX / Debit card / Flex spending plan debit cards', 'We are pleased to offer pre-payment discounts to our patients who pay before the time of service.'].map((item, idx) => <li key={idx} className="flex items-start gap-3" data-id="element-250">
                    <CheckCircle2 className="text-[#0D9488] flex-shrink-0 mt-1" size={20} data-id="element-251" />
                    <span className="text-slate-700 font-medium" data-id="element-252">{item}</span>
                  </li>)}
              </ul>

              <div className="bg-teal-50 p-6 rounded-lg border border-teal-100" data-id="element-253">
                <h3 className="font-bold text-[#0F2C59] mb-3 text-lg" data-id="element-254">
                  Third-Party Financing
                </h3>
                <p className="text-slate-600 text-sm leading-relaxed mb-6" data-id="element-255">
                  We are also pleased to offer Care Credit dental care
                  financing. Care Credit has been providing no-interest and
                  low-interest loan options for over 25 years to help us make
                  the care patients need comfortable for them and their
                  pocketbooks. We also partner with Sunbit for flexible
                  pay-over-time options.
                </p>
                <div className="flex flex-wrap gap-6 items-center" data-id="element-256">
                  {/* Placeholder for CareCredit Logo */}
                  <div className="text-2xl font-extrabold text-[#0F2C59] tracking-tighter flex items-center gap-1" data-id="element-257">
                    <span className="text-[#0D9488]" data-id="element-258">Care</span>Credit
                  </div>
                  {/* Placeholder for Sunbit Logo */}
                  <div className="text-2xl font-extrabold text-[#0F2C59] tracking-tighter flex items-center gap-1" data-id="element-259">
                    <div className="w-6 h-6 rounded-full bg-blue-500 flex items-center justify-center text-white text-xs" data-id="element-260">
                      S
                    </div>
                    sunbit
                  </div>
                </div>
              </div>
            </section>
          </div>

          {/* Final Conversion CTA */}
          <div className="mt-20 bg-[#0F2C59] rounded-[12px] p-10 md:p-16 text-center text-white shadow-2xl relative overflow-hidden" data-id="element-261">
            <div className="absolute top-0 right-0 w-64 h-64 bg-white/5 rounded-full -mr-32 -mt-32" data-id="element-262" />
            <div className="relative z-10" data-id="element-263">
              <h2 className="text-3xl md:text-4xl font-bold mb-6" data-id="element-264">
                Ready to Get Started?
              </h2>
              <p className="text-white/80 text-lg mb-10 max-w-2xl mx-auto" data-id="element-265">
                Don't let finances delay your dental care. Apply for flexible
                financing today or schedule a consultation to discuss your
                options with our team.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-266">
                <button className="bg-[#0D9488] hover:bg-[#0F766E] text-white px-10 py-4 rounded-lg font-bold transition-all shadow-lg shadow-teal-900/20" data-id="element-267">
                  Apply for Financing
                </button>
                <button className="border-2 border-white/30 hover:border-white text-white px-10 py-4 rounded-lg font-bold transition-all" data-id="element-268">
                  Book a Consultation
                </button>
              </div>
            </div>
          </div>
        </div>
      </main>

      <Footer data-id="element-269" />
    </div>;
}